// src/pages/SetupPage.jsx
// Client Data Input & Customization Hub - DVA Redesign Phase 1
// Design System: Adaptive Blue (#18365E), Interactive Orange (#FF6E4C), Montserrat
// Enhanced: Tab/View Toggle Pattern (Windsurfer Option C)
// FIXED: Removed hardcoded client_id '300' - now uses AuthContext session.client_id
// FIXED: Removed URL param fallback - session.client_id is the single source of truth

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  CheckCircle2,
  Clock,
  FileText,
  ClipboardList,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import ClientSelector from '../components/ClientSelector';
import ClientProfilePage from './ClientProfilePage';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export default function SetupPage() {
  const navigate = useNavigate();

  // ✅ Get session from AuthContext - this is the ONLY source of truth for client_id
  const { session, sessionLoading, sessionError } = useAuth();

  // ✅ Client ID comes exclusively from session
  const clientId = session?.client_id || null;

  // State
  const [profileData, setProfileData] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mode switching state
  const [viewMode, setViewMode] = useState('overview'); // 'overview' | 'edit-profile'
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch client profile and progress
  useEffect(() => {
    // Guard: Don't fetch if no clientId available yet
    if (!clientId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch profile
        const profileRes = await fetch(`${API_BASE}/api/v2/client-profile/${clientId}`);
        if (!profileRes.ok) throw new Error('Failed to fetch profile');
        const profileJson = await profileRes.json();
        setProfileData(profileJson.data);

        // Fetch progress
        const progressRes = await fetch(`${API_BASE}/api/v2/client-profile/${clientId}/progress`);
        if (!progressRes.ok) throw new Error('Failed to fetch progress');
        const progressJson = await progressRes.json();
        setProgressData(progressJson);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId]);

  // Handle client change (from ClientSelector - admin use case)
  const handleClientChange = (newClientId) => {
    // Note: This updates the selector UI but the actual client_id 
    // should be managed through AuthContext/session in production
    console.warn('Client change requested:', newClientId, '- This should update session.client_id');
  };

  // Mode transition handler with unsaved changes check
  const handleModeChange = (newMode) => {
    if (viewMode === 'edit-profile' && hasUnsavedChanges && newMode === 'overview') {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }

    // Smooth transition
    setIsTransitioning(true);
    setTimeout(() => {
      setViewMode(newMode);
      setIsTransitioning(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Reset unsaved changes when switching to overview
      if (newMode === 'overview') {
        setHasUnsavedChanges(false);
      }
    }, 150);
  };

  // Navigation helpers
  const goToClientProfile = () => handleModeChange('edit-profile');
  const goToSurvey = (type) => navigate(`/survey/${type}?client_id=${clientId}`);
  const goToDocuments = () => navigate(`/documents/${clientId}`);

  // Refresh data after profile save
  const refreshData = async () => {
    if (!clientId) return;

    try {
      const progressRes = await fetch(`${API_BASE}/api/v2/client-profile/${clientId}/progress`);
      if (progressRes.ok) {
        const progressJson = await progressRes.json();
        setProgressData(progressJson);
      }
    } catch (err) {
      console.error('Error refreshing progress:', err);
    }
  };

  // Show loading while session is being fetched
  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-[#18365E] animate-spin" />
          <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
            Loading session...
          </p>
        </div>
      </div>
    );
  }

  // Handle case where no client_id is available
  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAFAFA]">
        <div className="max-w-md p-6 bg-white rounded-xl shadow-lg border border-amber-200">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-6 h-6 text-amber-500" />
            <h3 className="text-lg" style={{ fontWeight: 700, color: '#18365E' }}>
              No Client Selected
            </h3>
          </div>
          <p className="text-sm text-[#717182] mb-4">
            {sessionError
              ? `Session error: ${sessionError}`
              : 'Please complete onboarding to set up your organization profile, or select a client from the dashboard.'
            }
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-all text-sm"
            style={{ fontWeight: 600 }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-[#18365E] animate-spin" />
          <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
            Loading setup data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#FAFAFA]">
        <div className="max-w-md p-6 bg-white rounded-xl shadow-lg border border-red-200">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-6 h-6 text-red-500" />
            <h3 className="text-lg" style={{ fontWeight: 700, color: '#18365E' }}>
              Error Loading Data
            </h3>
          </div>
          <p className="text-sm text-[#717182] mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#18365E] text-white rounded-lg hover:bg-[#18365E]/90 transition-all text-sm"
            style={{ fontWeight: 600 }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const overallPct = progressData?.completionPct || 0;
  const sections = progressData?.sections || {};

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header - Changes based on mode */}
      <SetupHeader
        mode={viewMode}
        onModeChange={handleModeChange}
        progress={overallPct}
        hasUnsavedChanges={hasUnsavedChanges}
        clientId={clientId}
        onClientChange={handleClientChange}
        profileData={profileData}
      />

      {/* Main Content - Conditional Rendering */}
      <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        {viewMode === 'overview' ? (
          // Overview Mode - Original Dashboard
          <OverviewDashboard
            overallPct={overallPct}
            sections={sections}
            progressData={progressData}
            profileData={profileData}
            onEditProfile={goToClientProfile}
            goToSurvey={goToSurvey}
            goToDocuments={goToDocuments}
            clientId={clientId}
          />
        ) : (
          // Edit Profile Mode - Will integrate ClientProfilePage
          <ProfileEditor
            clientId={clientId}
            onSave={() => {
              setHasUnsavedChanges(false);
              handleModeChange('overview');
              refreshData();
            }}
            onCancel={() => handleModeChange('overview')}
            onDirty={() => setHasUnsavedChanges(true)}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// HEADER COMPONENT - Adapts based on mode
// ============================================================================

function SetupHeader({ mode, onModeChange, progress, hasUnsavedChanges, clientId, onClientChange, profileData }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-[1440px] mx-auto px-6 py-4">
        {mode === 'overview' ? (
          // Overview Mode Header
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl mb-1" style={{ fontWeight: 700, color: '#18365E', fontFamily: 'Montserrat, sans-serif' }}>
                Client Data Input & Customization
              </h1>
              <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
                Complete your organization profile to unlock personalized insights
              </p>
            </div>
            <ClientSelector
              currentClientId={clientId}
              onClientChange={onClientChange}
            />
          </div>
        ) : (
          // Edit Profile Mode Header
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              {/* Back button + Title */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => onModeChange('overview')}
                  className="flex items-center gap-2 text-[#717182] hover:text-[#18365E] transition-colors"
                  style={{ fontWeight: 500 }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Overview</span>
                </button>
                <div className="h-6 w-px bg-gray-300" />
                <h2 className="text-lg" style={{ fontWeight: 600, color: '#18365E' }}>
                  Edit Client Profile
                </h2>
                {profileData?.organization_name && (
                  <span className="text-sm text-[#717182]">
                    • {profileData.organization_name}
                  </span>
                )}
              </div>

              {/* Mini progress indicator */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#18365E] to-[#FF6E4C] transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-sm text-[#717182]" style={{ fontWeight: 600 }}>
                    {progress}%
                  </span>
                </div>
              </div>
            </div>

            {/* Unsaved changes warning */}
            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4" />
                <span style={{ fontWeight: 500 }}>You have unsaved changes</span>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

// ============================================================================
// OVERVIEW DASHBOARD - Original Setup Page Content
// ============================================================================

function OverviewDashboard({
  overallPct,
  sections,
  progressData,
  profileData,
  onEditProfile,
  goToSurvey,
  goToDocuments,
  clientId
}) {
  return (
    <div className="max-w-[1440px] mx-auto px-6 py-8">
      {/* Progress Widget */}
      <div className="mb-8 bg-gradient-to-br from-white to-[#18365E]/5 rounded-2xl p-6 shadow-lg border-2 border-[#18365E]/20">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-lg mb-2" style={{ fontWeight: 700, color: '#18365E' }}>
              Setup Progress
            </h2>
            <p className="text-sm text-[#717182] mb-4" style={{ fontWeight: 500 }}>
              {overallPct === 100
                ? '✅ All sections complete! Your profile is ready.'
                : `${overallPct}% complete - Keep going to unlock full DVA capabilities`
              }
            </p>

            {/* Progress Bar */}
            <div className="w-full h-3 bg-[#F5F7FA] rounded-full overflow-hidden shadow-inner">
              <div
                className="h-full bg-gradient-to-r from-[#18365E] to-[#FF6E4C] rounded-full transition-all duration-1000 shadow-md"
                style={{ width: `${overallPct}%` }}
              />
            </div>
          </div>

          {/* Circular Progress */}
          <div className="ml-8 flex flex-col items-center">
            <div className="relative w-24 h-24">
              <svg className="transform -rotate-90 w-24 h-24">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="#F5F7FA"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="url(#progressGradient)"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${2 * Math.PI * 40 * (1 - overallPct / 100)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000"
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#18365E" />
                    <stop offset="100%" stopColor="#FF6E4C" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl" style={{ fontWeight: 700, color: '#18365E' }}>
                  {overallPct}%
                </span>
              </div>
            </div>
            <p className="text-xs text-[#717182] mt-2" style={{ fontWeight: 500 }}>
              Last updated
            </p>
            <p className="text-xs text-[#717182]" style={{ fontWeight: 600 }}>
              {progressData?.lastUpdated
                ? new Date(progressData.lastUpdated).toLocaleString()
                : 'Never'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Section Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* 1. Client Profile */}
        <SetupCard
          icon={<Building2 className="w-6 h-6" />}
          title="Client Profile"
          description="Organization details, contact info, and operational data"
          status={sections.clientProfile?.complete ? 'complete' : 'in_progress'}
          progress={sections.clientProfile?.pct || 0}
          details={sections.clientProfile?.details || 'No profile data yet'}
          actionLabel="Edit Profile"
          onAction={onEditProfile}
        />

        {/* 2. Baseline Strategy (Phase 2 - Coming Soon) */}
        <SetupCard
          icon={<ClipboardList className="w-6 h-6" />}
          title="Baseline Strategy"
          description="Strategic priorities and organizational goals"
          status="coming_soon"
          progress={0}
          details="Coming in Phase 2"
          actionLabel="Coming Soon"
          disabled
        />

        {/* 3. Surveys */}
        <SetupCard
          icon={<FileText className="w-6 h-6" />}
          title="Surveys"
          description="Complete three assessments: Strategy, Capabilities, and Readiness"
          status={sections.surveys?.complete ? 'complete' : 'in_progress'}
          progress={sections.surveys?.pct || 0}
          details={sections.surveys?.details || 'No surveys completed'}
          actionLabel="View Surveys"
          onAction={() => {
            // Show survey options
            const surveyType = window.prompt(
              'Which survey?\n1. strategy\n2. capabilities\n3. readiness',
              'strategy'
            );
            if (surveyType) goToSurvey(surveyType);
          }}
          breakdown={sections.surveys?.breakdown}
        />

        {/* 4. Documents */}
        <SetupCard
          icon={<FileText className="w-6 h-6" />}
          title="Documents"
          description="Upload strategic plans, org charts, and other context documents"
          status={sections.documents?.complete ? 'complete' : 'not_started'}
          progress={sections.documents?.pct || 0}
          details={sections.documents?.details || 'No documents uploaded'}
          actionLabel="Manage Documents"
          onAction={goToDocuments}
        />
      </div>

      {/* Next Steps CTA */}
      {overallPct < 100 && (
        <div className="mt-8 p-6 bg-gradient-to-r from-[#FF6E4C]/10 to-[#FF6E4C]/5 rounded-xl border border-[#FF6E4C]/20">
          <div className="flex items-start gap-3">
            <Clock className="w-6 h-6 text-[#FF6E4C] mt-1" />
            <div className="flex-1">
              <h3 className="text-lg mb-2" style={{ fontWeight: 700, color: '#18365E' }}>
                Next Steps
              </h3>
              <p className="text-sm text-[#717182] mb-4" style={{ fontWeight: 500 }}>
                Complete your profile to unlock AI-powered ROI analysis, strategic roadmaps, and executive summaries.
              </p>
              <button
                onClick={onEditProfile}
                className="flex items-center gap-2 px-6 py-3 bg-[#FF6E4C] text-white rounded-lg hover:bg-[#FF6E4C]/90 transition-all shadow-md hover:shadow-lg"
                style={{ fontWeight: 600 }}
              >
                Continue Setup
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Info (Remove in production) */}
      {import.meta.env.DEV && (
        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
          <p className="text-xs text-gray-600 mb-2" style={{ fontWeight: 700 }}>
            Debug Info (Dev Only):
          </p>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify({ clientId, overallPct, sections }, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PROFILE EDITOR - Integrates ClientProfilePage
// ============================================================================

function ProfileEditor({ clientId, onSave, onCancel, onDirty }) {
  // Track initial load to set editing state
  React.useEffect(() => {
    // When embedded, always start in editing mode
    onDirty?.();
  }, [onDirty]);

  const handleSaveSuccess = async () => {
    // Call parent's onSave which returns to overview and refreshes progress
    await onSave?.();
  };

  const handleCancel = () => {
    // Call parent's onCancel which returns to overview
    onCancel?.();
  };

  return (
    <div className="animate-fadeIn">
      <ClientProfilePage
        clientId={clientId}
        embedded={true}
        onSaveSuccess={handleSaveSuccess}
        onCancelEdit={handleCancel}
      />
    </div>
  );
}

// ============================================================================
// REUSABLE SETUP CARD COMPONENT
// ============================================================================

function SetupCard({
  icon,
  title,
  description,
  status,
  progress,
  details,
  actionLabel,
  onAction,
  disabled,
  breakdown
}) {
  const getStatusConfig = () => {
    switch (status) {
      case 'complete':
        return {
          bgColor: 'from-[#6FCF97]/10 to-[#6FCF97]/5',
          borderColor: 'border-[#6FCF97]/30',
          iconColor: 'text-[#6FCF97]',
          icon: <CheckCircle2 className="w-5 h-5" />
        };
      case 'in_progress':
        return {
          bgColor: 'from-[#FF6E4C]/10 to-[#FF6E4C]/5',
          borderColor: 'border-[#FF6E4C]/30',
          iconColor: 'text-[#FF6E4C]',
          icon: <Clock className="w-5 h-5" />
        };
      case 'coming_soon':
        return {
          bgColor: 'from-gray-100 to-gray-50',
          borderColor: 'border-gray-200',
          iconColor: 'text-gray-400',
          icon: <Clock className="w-5 h-5" />
        };
      default:
        return {
          bgColor: 'from-white to-[#F5F7FA]',
          borderColor: 'border-[#e5e7eb]',
          iconColor: 'text-[#717182]',
          icon: <AlertCircle className="w-5 h-5" />
        };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={`bg-gradient-to-br ${config.bgColor} rounded-xl p-6 border-2 ${config.borderColor} shadow-md hover:shadow-lg transition-all ${disabled ? 'opacity-60' : 'hover:-translate-y-1'}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-white shadow-sm ${config.iconColor}`}>
            {icon}
          </div>
          <div className="flex-1">
            <h3 className="text-lg mb-1" style={{ fontWeight: 700, color: '#18365E' }}>
              {title}
            </h3>
            <p className="text-sm text-[#717182]" style={{ fontWeight: 500 }}>
              {description}
            </p>
          </div>
        </div>
        <div className={`flex items-center gap-2 ${config.iconColor}`}>
          {config.icon}
        </div>
      </div>

      {/* Progress Bar */}
      {status !== 'coming_soon' && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-[#717182]" style={{ fontWeight: 600 }}>
              Progress
            </span>
            <span className="text-xs" style={{ fontWeight: 700, color: '#18365E' }}>
              {progress}%
            </span>
          </div>
          <div className="w-full h-2 bg-[#F5F7FA] rounded-full overflow-hidden">
            <div
              className={`h-full ${status === 'complete' ? 'bg-[#6FCF97]' : 'bg-[#FF6E4C]'} rounded-full transition-all duration-500`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Details */}
      <p className="text-sm text-[#717182] mb-4" style={{ fontWeight: 500 }}>
        {details}
      </p>

      {/* Survey Breakdown (if available) */}
      {breakdown && (
        <div className="mb-4 p-3 bg-white/50 rounded-lg">
          <p className="text-xs text-[#717182] mb-2" style={{ fontWeight: 600 }}>
            Survey Breakdown:
          </p>
          <div className="space-y-1">
            {Object.entries(breakdown).map(([key, data]) => (
              <div key={key} className="flex items-center justify-between text-xs">
                <span className="text-[#717182] capitalize">{key}:</span>
                <span className={data.complete ? 'text-[#6FCF97] font-bold' : 'text-[#717182]'}>
                  {data.answered}/{data.total} {data.complete ? '✓' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <button
        onClick={onAction}
        disabled={disabled}
        className={`w-full px-4 py-2.5 rounded-lg transition-all text-sm ${
          disabled
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : status === 'complete'
            ? 'bg-white text-[#18365E] border-2 border-[#18365E]/20 hover:bg-[#18365E]/5'
            : 'bg-[#FF6E4C] text-white hover:bg-[#FF6E4C]/90 shadow-md hover:shadow-lg'
        }`}
        style={{ fontWeight: 600 }}
      >
        {actionLabel}
      </button>
    </div>
  );
}